// src/components/common/footer/ResponsiveFooterNav.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMessageCount } from '../../../contexts/MessageCountContext';
import { useOfferCount } from '../../../contexts/OfferCountContext';
import { useContractCount } from '../../../contexts/ContractCountContext';
import { listUpcomingHoldRequests } from '../../../api/castingSummaries';
import { useAdminChatFollowupSummary } from '../chat/followups/chatFollowupApi';
import { buildAdminChatMenuBadges } from '../../../utils/adminChatMenuBadges';

import '../../../css/common/footer/ResponsiveFooterNav.css';

// 汎用 FontAwesome アイコン + 補助テキスト（下段ラベル）で構成する。
// オリジナル SVG は廃止し、直感的な汎用アイコンへ統一。
const DASHBOARD_ICON_CLASS = 'fa-solid fa-grip';
const ARTIST_CHAT_ICON_CLASS = 'fa-solid fa-comments';
const GROUP_CHAT_ICON_CLASS = 'fa-solid fa-users';
const EVENT_CHAT_ICON_CLASS = 'fa-solid fa-message';
const OFFER_ICON_CLASS = 'fa-solid fa-paper-plane';
const CONTRACT_ICON_CLASS = 'fa-solid fa-file-signature';
const LONG_PRESS_DELAY_MS = 560;
const CONTEXT_MENU_BLOCK_MS = 900;

const clampBadge = (value) => (value > 99 ? '99+' : String(value));

const countPendingOffers = (payload) => {
  const list = payload?.holdRequests || payload?.hold_requests || [];
  return (Array.isArray(list) ? list : []).filter(
    (item) => String(item?.status || '').toLowerCase() === 'pending'
  ).length;
};

const isActivePath = (pathname, { exact, prefixes = [] } = {}) => {
  if (!pathname) return false;
  if (exact) return pathname === exact;
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const resolveAdminFooterHomePath = (pathname = '') => {
  const path = String(pathname || '');
  if (path.startsWith('/admin/workspaces/finance')) {
    return '/admin/workspaces/finance/dashboard';
  }
  if (path.startsWith('/admin/workspaces/concierge')) {
    return '/admin/workspaces/concierge/dashboard';
  }
  return '/admin/workspaces/event-operations/dashboard';
};

const resolveArtistFooterHomePath = (pathname = '') => {
  const path = String(pathname || '');
  if (path.startsWith('/artist/workspaces/contracts')) {
    return '/artist/workspaces/contracts/dashboard';
  }
  if (path.startsWith('/artist/workspaces/concierge')) {
    return '/artist/workspaces/concierge/dashboard';
  }
  return '/artist/workspaces/event-operations/dashboard';
};

const buildAdminItems = ({
  pathname,
  navigate,
  offerTaskCount,
  chatMenuBadges,
}) => {
  const dashboardHref = resolveAdminFooterHomePath(pathname);
  const isGroupChatPath = isActivePath(pathname, { prefixes: ['/admin/workspaces/communications/group-chat'] });
  const chatItem = isGroupChatPath
    ? {
        key: 'groupChat',
        label: 'グループチャット',
        shortLabel: 'グループ',
        iconClass: GROUP_CHAT_ICON_CLASS,
        href: '/admin/workspaces/communications/group-chat',
        active: true,
        badgeCount: Number(chatMenuBadges?.group?.count || 0),
        badgeTone: chatMenuBadges?.group?.toneClassName || '',
        badgeClamp: true,
        onClick: () =>
          navigate('/admin/workspaces/communications/group-chat', {
            replace: true,
            state: { reset: Date.now(), chatType: 'group' },
          }),
      }
    : {
        key: 'artistChat',
        label: 'アーティストチャット',
        shortLabel: 'アーティスト',
        iconClass: ARTIST_CHAT_ICON_CLASS,
        href: '/admin/workspaces/communications/artist-chat',
        active: isActivePath(pathname, { prefixes: ['/admin/workspaces/communications/artist-chat'] }),
        badgeCount: Number(chatMenuBadges?.artist?.count || 0),
        badgeTone: chatMenuBadges?.artist?.toneClassName || '',
        badgeClamp: true,
        onClick: () =>
          navigate('/admin/workspaces/communications/artist-chat', {
            replace: true,
            state: { reset: Date.now(), chatType: 'artist' },
          }),
      };
  return [
    {
      key: 'dashboard',
      label: 'ホーム',
      shortLabel: 'ホーム',
      iconClass: DASHBOARD_ICON_CLASS,
      href: dashboardHref,
      active: pathname === dashboardHref,
    },
    chatItem,
    {
      key: 'eventChat',
      label: 'イベントチャット',
      shortLabel: 'イベント',
      iconClass: EVENT_CHAT_ICON_CLASS,
      href: '/admin/workspaces/event-operations/event-chat',
      active: isActivePath(pathname, { prefixes: ['/admin/workspaces/event-operations/event-chat'] }),
      badgeCount: Number(chatMenuBadges?.event?.count || 0),
      badgeTone: chatMenuBadges?.event?.toneClassName || '',
      badgeClamp: true,
      onClick: () =>
        navigate('/admin/workspaces/event-operations/event-chat', {
          replace: true,
          state: { reset: Date.now(), chatType: 'event' },
        }),
    },
    {
      key: 'offers',
      label: 'オファーリスト',
      shortLabel: 'オファー',
      iconClass: OFFER_ICON_CLASS,
      href: '/admin/workspaces/casting/offers',
      longPressHref: '/admin/workspaces/casting/offers/new',
      active: isActivePath(pathname, {
        prefixes: [
          '/admin/workspaces/casting/offers',
          '/admin/workspaces/casting/offers/new',
        ],
      }),
      badgeCount: Number(offerTaskCount || 0),
      badgeTone: 'sidebar-count-badge--alert',
      badgeAriaLabel: '回答待ち',
    },
    {
      key: 'contracts',
      label: '本契約リスト',
      shortLabel: '本契約',
      iconClass: CONTRACT_ICON_CLASS,
      href: '/admin/workspaces/contracts/history',
      longPressHref: '/admin/workspaces/contracts/form',
      longPressOptions: { state: { reset: true } },
      active: isActivePath(pathname, { prefixes: ['/admin/workspaces/contracts'] }),
    },
  ];
};

const buildArtistItems = ({
  pathname,
  navigate,
  chatMode,
  artistMessageCount,
  artistGroupChatMessageCount,
  eventChatMessageCount,
  offerCount,
  contractCount,
  eventChatHasMention = false,
  groupChatHasMention = false,
}) => {
  const dashboardHref = resolveArtistFooterHomePath(pathname);
  const isArtistChatPath = isActivePath(pathname, { prefixes: ['/artist/workspaces/communications/chat'] });
  const isGroupChatMode = isArtistChatPath && chatMode === 'group';
  const chatItem = isGroupChatMode
    ? {
        key: 'groupChat',
        label: 'グループチャット',
        shortLabel: 'グループ',
        iconClass: GROUP_CHAT_ICON_CLASS,
        href: '/artist/workspaces/communications/chat',
        active: true,
        badgeCount: Number(artistGroupChatMessageCount || 0),
        badgeTone: groupChatHasMention ? 'sidebar-count-badge--mention' : 'sidebar-count-badge--alert',
        badgeClamp: true,
      }
    : {
        key: 'artistChat',
        label: 'チャット',
        shortLabel: 'チャット',
        iconClass: ARTIST_CHAT_ICON_CLASS,
        href: '/artist/workspaces/communications/chat',
        active: isArtistChatPath,
        badgeCount: Number(artistMessageCount || 0),
        badgeTone: 'sidebar-count-badge--alert',
        badgeClamp: true,
      };
  return [
    {
      key: 'dashboard',
      label: 'ホーム',
      shortLabel: 'ホーム',
      iconClass: DASHBOARD_ICON_CLASS,
      href: dashboardHref,
      active: pathname === dashboardHref,
    },
    chatItem,
    {
      key: 'eventChat',
      label: 'イベントチャット',
      shortLabel: 'イベント',
      iconClass: EVENT_CHAT_ICON_CLASS,
      href: '/artist/workspaces/event-operations/event-chat',
      active: isActivePath(pathname, { prefixes: ['/artist/workspaces/event-operations/event-chat'] }),
      badgeCount: Number(eventChatMessageCount || 0),
      badgeTone: eventChatHasMention ? 'sidebar-count-badge--mention' : 'sidebar-count-badge--alert',
      badgeClamp: true,
      onClick: () =>
        navigate('/artist/workspaces/event-operations/event-chat', {
          replace: true,
          state: { reset: true },
        }),
    },
    {
      key: 'offers',
      label: 'オファーリスト',
      shortLabel: 'オファー',
      iconClass: OFFER_ICON_CLASS,
      href: '/artist/workspaces/casting/offers',
      active: isActivePath(pathname, {
        prefixes: ['/artist/workspaces/casting/offers'],
      }),
      badgeCount: Number(offerCount || 0),
      badgeTone: 'sidebar-count-badge--alert',
    },
    {
      key: 'contracts',
      label: '本契約リスト',
      shortLabel: '本契約',
      iconClass: CONTRACT_ICON_CLASS,
      href: '/artist/workspaces/contracts/dashboard',
      active: isActivePath(pathname, {
        prefixes: ['/artist/workspaces/contracts'],
      }),
      badgeCount: Number(contractCount || 0),
      badgeTone: 'sidebar-count-badge--alert',
    },
  ];
};

function ResponsiveFooterNav({ chatMode = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname || '';
  const isAdminTaskWorkspace = pathname.startsWith('/admin/workspaces/task-management');
  const isAdmin =
    !isAdminTaskWorkspace &&
    (pathname.startsWith('/admin/workspaces') || pathname.startsWith('/admin/settings'));
  const isArtist = pathname.startsWith('/artist/workspaces') || pathname.startsWith('/artist/settings');
  const longPressRef = useRef({
    timer: null,
    clearTimer: null,
    contextMenuTimer: null,
    ignoreClick: false,
    suppressContextMenu: false,
  });

  const {
    adminMessageCount = 0,
    artistMessageCount = 0,
    artistGroupChatMessageCount = 0,
    adminGroupChatMessageCount = 0,
    eventChatMessageCount = 0,
    adminArtistChatTone = null,
    adminGroupChatTone = null,
    adminEventChatTone = null,
    eventChatHasMention = false,
    groupChatHasMention = false,
  } = useMessageCount?.() || {};
  const { offerCount = 0 } = useOfferCount?.() || {};
  const { contractCount = 0 } = useContractCount?.() || {};
  const [adminOfferTaskCount, setAdminOfferTaskCount] = useState(0);
  const { summary: chatFollowupSummary } = useAdminChatFollowupSummary({ enabled: isAdmin });
  const chatMenuBadges = useMemo(
    () =>
      buildAdminChatMenuBadges({
        artistUnreadCount: adminMessageCount,
        artistUnreadTone: adminArtistChatTone,
        eventUnreadCount: eventChatMessageCount,
        eventUnreadTone: adminEventChatTone,
        eventHasMention: eventChatHasMention,
        groupUnreadCount: adminGroupChatMessageCount,
        groupUnreadTone: adminGroupChatTone,
        groupHasMention: groupChatHasMention,
        followupSummary: chatFollowupSummary,
      }),
    [
      adminArtistChatTone,
      adminEventChatTone,
      adminGroupChatMessageCount,
      adminGroupChatTone,
      adminMessageCount,
      chatFollowupSummary,
      eventChatMessageCount,
      eventChatHasMention,
      groupChatHasMention,
    ]
  );

  const fetchAdminOfferTaskCount = useCallback(async () => {
    if (!isAdmin) {
      setAdminOfferTaskCount(0);
      return;
    }
    try {
      const data = await listUpcomingHoldRequests();
      setAdminOfferTaskCount(countPendingOffers(data));
    } catch (_) {
      // ignore
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchAdminOfferTaskCount();
  }, [fetchAdminOfferTaskCount, pathname]);

  useEffect(() => {
    const onRefresh = () => fetchAdminOfferTaskCount();
    window.addEventListener('offers:refresh-task-count', onRefresh);
    return () => window.removeEventListener('offers:refresh-task-count', onRefresh);
  }, [fetchAdminOfferTaskCount]);

  const items = useMemo(() => {
    if (isAdmin) {
      return buildAdminItems({
        pathname,
        navigate,
        offerTaskCount: adminOfferTaskCount,
        chatMenuBadges,
      });
    }
    if (isArtist) {
      return buildArtistItems({
        pathname,
        navigate,
        chatMode,
        artistMessageCount,
        artistGroupChatMessageCount,
        eventChatMessageCount,
        offerCount,
        contractCount,
        eventChatHasMention,
        groupChatHasMention,
      });
    }
    return [];
  }, [
    adminOfferTaskCount,
    chatMenuBadges,
    artistGroupChatMessageCount,
    artistMessageCount,
    chatMode,
    contractCount,
    eventChatMessageCount,
    eventChatHasMention,
    groupChatHasMention,
    isAdmin,
    isArtist,
    navigate,
    offerCount,
    pathname,
  ]);

  const openSidebar = useCallback(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    if (!body) return;
    const isMobile =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 700px)').matches;
    if (isMobile) {
      if (!body.classList.contains('sidebar-mobile-open')) {
        body.classList.add('sidebar-mobile-open');
      }
      window.dispatchEvent(
        new CustomEvent('casticart:sidebar-visibility', { detail: { visible: true } })
      );
      return;
    }
    if (body.classList.contains('sidebar-collapsed')) {
      body.classList.remove('sidebar-collapsed');
      try {
        localStorage.setItem('layout.sidebarCollapsed', 'false');
      } catch {}
      window.dispatchEvent(
        new CustomEvent('casticart:toggle-sidebar', { detail: { collapsed: false } })
      );
    }
    window.dispatchEvent(
      new CustomEvent('casticart:sidebar-visibility', { detail: { visible: true } })
    );
  }, []);

  const blockContextMenu = useCallback(() => {
    const state = longPressRef.current;
    if (state.contextMenuTimer) {
      clearTimeout(state.contextMenuTimer);
    }
    state.suppressContextMenu = true;
    state.contextMenuTimer = setTimeout(() => {
      state.suppressContextMenu = false;
      state.contextMenuTimer = null;
    }, CONTEXT_MENU_BLOCK_MS);
  }, []);

  useEffect(() => {
    const handleContextMenu = (event) => {
      if (!longPressRef.current.suppressContextMenu) return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('contextmenu', handleContextMenu, true);
    return () => window.removeEventListener('contextmenu', handleContextMenu, true);
  }, []);

  const clearLongPress = useCallback(() => {
    const state = longPressRef.current;
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
  }, []);

  const startLongPress = useCallback((item) => {
    const state = longPressRef.current;
    clearLongPress();
    state.ignoreClick = false;
    state.timer = setTimeout(() => {
      state.ignoreClick = true;
      blockContextMenu();
      if (item?.longPressHref) {
        navigate(item.longPressHref, item.longPressOptions || undefined);
      } else {
        openSidebar();
      }
      if (state.clearTimer) {
        clearTimeout(state.clearTimer);
      }
      state.clearTimer = setTimeout(() => {
        state.ignoreClick = false;
        state.clearTimer = null;
      }, 1000);
    }, LONG_PRESS_DELAY_MS);
  }, [blockContextMenu, clearLongPress, navigate, openSidebar]);

  if (!items.length) return null;

  return (
    <nav className="footer-quick-nav" aria-label="簡易フッターメニュー">
      <div className="footer-quick-nav__row">
        {items.map((item) => {
          const showBadge = Number(item.badgeCount || 0) > 0;
          const badgeLabel = item.badgeClamp ? clampBadge(item.badgeCount) : String(item.badgeCount);
          return (
            <button
              key={item.key}
              type="button"
              className={`footer-quick-nav__item${item.active ? ' is-active' : ''}`}
              aria-current={item.active ? 'page' : undefined}
              aria-label={item.label}
              onPointerDown={(event) => {
                if (event.pointerType === 'mouse' && event.button !== 0) return;
                startLongPress(item);
              }}
              onPointerUp={clearLongPress}
              onPointerLeave={clearLongPress}
              onPointerCancel={clearLongPress}
              onContextMenu={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={() => {
                if (longPressRef.current.ignoreClick) {
                  longPressRef.current.ignoreClick = false;
                  return;
                }
                if (item.onClick) {
                  item.onClick();
                } else if (item.href) {
                  navigate(item.href);
                }
              }}
            >
              <span className="footer-quick-nav__icon" aria-hidden="true">
                <i className={`footer-quick-nav__fa ${item.iconClass}`} />
                {showBadge && (
                  <span
                    className={`footer-quick-nav__badge ${item.badgeTone || ''}`.trim()}
                    aria-label={`${item.badgeAriaLabel || '未読'}${badgeLabel}件`}
                  >
                    {badgeLabel}
                  </span>
                )}
              </span>
              <span className="footer-quick-nav__label" aria-hidden="true">
                {item.shortLabel || item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default ResponsiveFooterNav;
